/**
 * «Enviar» un presupuesto o una factura — encargo del propietario 2026-10-22.
 *
 * El correo lo manda el usuario desde SU cuenta (Gmail, Outlook/Hotmail o el
 * programa del ordenador o del móvil). Faktelio no envía nada: no hay coste por
 * usuario ni se consume la cuota de «Emails documentos».
 *
 * ```text
 * COMPARTIR   navigator.share con el PDF como fichero: móvil, y Chrome, Edge o
 *             Safari en el ordenador. El PDF va ADJUNTO de verdad
 * ENLACES     Gmail, Outlook y mailto, con destinatario, asunto y texto; el PDF
 *             se descarga a la vez, porque ningún enlace puede adjuntarlo
 * WHATSAPP    wa.me con el teléfono de la ficha y el texto (2026-10-23): el
 *             mismo trato que los enlaces. Telegram no tiene botón propio: su
 *             enlace no elige contacto ni adjunta, y ya sale en COMPARTIR
 * ```
 *
 * El PDF se pide AL ABRIR, no al pulsar: `navigator.share` sólo funciona
 * dentro del gesto del usuario, y Safari lo da por perdido si antes hay que
 * esperar a la red. Por lo mismo las ventanas de Gmail y Outlook se abren sin
 * nada que esperar delante, y no las bloquea el navegador.
 *
 * Sólo hay documento de lo CONGELADO (presupuesto enviado, factura emitida):
 * es lo que recibe el cliente y lo que queda guardado. Un borrador de
 * presupuesto se marca primero como enviado, aquí mismo.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api/cliente";
import { enlaceRedactar, enlaceWhatsApp, telefonoWhatsApp, type ServicioCorreo } from "../enlaces/redactar-correo";
import { t } from "../i18n/es";
import { Aviso, Boton, Campo } from "../ui/primitivas";

/** Entrega el fichero al navegador como una descarga normal, con su nombre. */
function guardar(fichero: File): void {
  const url = URL.createObjectURL(fichero);
  const a = document.createElement("a");
  a.href = url;
  a.download = fichero.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // En el siguiente turno: revocarla en el mismo instante cancela la descarga
  // en algunos navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Si este navegador sabe compartir ESTE fichero (no basta con que exista `share`). */
function sePuedeCompartir(fichero: File): boolean {
  try {
    return typeof navigator.canShare === "function" && navigator.canShare({ files: [fichero] });
  } catch {
    return false;
  }
}

export function EnviarDocumento({
  documento,
  id,
  asunto,
  cuerpo,
  correoCliente,
  telefonoCliente = null,
  paisCliente = "ES",
  hayConexion,
  pendienteDeMarcar = false,
  onMarcarEnviado,
  onSesionCaducada,
}: {
  documento: "quotes" | "invoices";
  id: string;
  asunto: string;
  cuerpo: string;
  /** El email guardado en la ficha del cliente, si lo tiene. */
  correoCliente: string | null;
  /** El teléfono de la ficha y su país, para abrir el chat de WhatsApp. */
  telefonoCliente?: string | null;
  paisCliente?: string;
  hayConexion: boolean;
  /** Un presupuesto todavía en borrador: primero se marca como enviado. */
  pendienteDeMarcar?: boolean;
  onMarcarEnviado?: () => void;
  onSesionCaducada: () => void;
}) {
  const [fichero, setFichero] = useState<File | null>(null);
  const [error, setError] = useState(false);
  const [para, setPara] = useState(correoCliente ?? "");
  const [descargado, setDescargado] = useState<{ nombre: string; enChat: boolean } | null>(null);

  // La lista de clientes puede llegar después: se rellena si sigue vacío, sin
  // pisar lo que el usuario haya escrito.
  useEffect(() => {
    if (correoCliente !== null) setPara((actual) => (actual === "" ? correoCliente : actual));
  }, [correoCliente]);

  const preparar = useCallback(async () => {
    setError(false);
    const r = await api.descargarPdf(documento, id);
    if (r.ok) {
      setFichero(new File([r.datos.contenido], r.datos.nombre, { type: "application/pdf" }));
      return;
    }
    if (r.estado === 401) return onSesionCaducada();
    setError(true);
  }, [documento, id, onSesionCaducada]);

  useEffect(() => {
    setFichero(null);
    if (!pendienteDeMarcar) void preparar();
  }, [pendienteDeMarcar, preparar]);

  if (pendienteDeMarcar) {
    return (
      <>
        <p className="ayuda-neutra">{t.enviarDocumento.borrador}</p>
        <Boton onClick={onMarcarEnviado} deshabilitado={!hayConexion || onMarcarEnviado === undefined}>
          {t.enviarDocumento.marcarYPreparar}
        </Boton>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Aviso tono="error">{hayConexion ? t.enviarDocumento.errorPdf : t.comun.sinRed}</Aviso>
        <Boton variante="secundario" onClick={() => void preparar()} deshabilitado={!hayConexion}>
          {t.enviarDocumento.reintentar}
        </Boton>
      </>
    );
  }

  if (fichero === null) return <p className="ayuda-neutra">{t.enviarDocumento.preparando}</p>;

  const borrador = { para, asunto, cuerpo };
  const whatsapp = telefonoWhatsApp(telefonoCliente, paisCliente);

  async function compartir(f: File): Promise<void> {
    try {
      await navigator.share({ files: [f], title: asunto, text: cuerpo });
    } catch {
      // Cancelar el menú de compartir también llega aquí: no es un error que
      // haya que enseñar, y los enlaces de abajo siguen ahí.
    }
  }

  function abrir(servicio: ServicioCorreo | "whatsapp", f: File): void {
    guardar(f);
    setDescargado({ nombre: f.name, enChat: servicio === "whatsapp" });
    const url = servicio === "whatsapp" ? enlaceWhatsApp(whatsapp, cuerpo) : enlaceRedactar(servicio, borrador);
    if (servicio === "local") window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <>
      <p className="ayuda-neutra">{t.enviarDocumento.detalle}</p>
      <Campo
        id={`enviar-para-${id}`}
        etiqueta={t.enviarDocumento.para}
        valor={para}
        onChange={setPara}
        tipo="email"
        inputMode="email"
        autoComplete="off"
        requerido={false}
        ayuda={t.enviarDocumento.ayudaPara}
      />
      {sePuedeCompartir(fichero) ? (
        <>
          <Boton onClick={() => void compartir(fichero)}>{t.enviarDocumento.compartir}</Boton>
          <p className="ayuda-neutra">{t.enviarDocumento.ayudaCompartir}</p>
        </>
      ) : null}
      <p className="ayuda-neutra">{t.enviarDocumento.desdeNavegador}</p>
      <div className="fila-botones">
        <Boton variante="secundario" onClick={() => abrir("gmail", fichero)}>
          {t.enviarDocumento.gmail}
        </Boton>
        <Boton variante="secundario" onClick={() => abrir("outlook", fichero)}>
          {t.enviarDocumento.outlook}
        </Boton>
        <Boton variante="secundario" onClick={() => abrir("local", fichero)}>
          {t.enviarDocumento.local}
        </Boton>
      </div>
      <p className="ayuda-neutra">
        {whatsapp === "" ? t.enviarDocumento.whatsappSinNumero : t.enviarDocumento.whatsappAl(`+${whatsapp}`)}
      </p>
      <div className="fila-botones">
        <Boton variante="secundario" onClick={() => abrir("whatsapp", fichero)}>
          {t.enviarDocumento.whatsapp}
        </Boton>
      </div>
      {descargado !== null ? (
        <Aviso tono="info">
          {descargado.enChat
            ? t.enviarDocumento.descargadoChat(descargado.nombre)
            : t.enviarDocumento.descargado(descargado.nombre)}
        </Aviso>
      ) : null}
    </>
  );
}
